'use client';

import { ActiveAudioItemProvider, AudioPlayer } from 'components';
import type { SearchResult } from 'conversational-search-client';
import type { JSX } from 'react';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useTopResults } from '../../_hooks';
import { AllResultsSection } from './AllResultsSection';
import { AudioResultCard } from './AudioResultCard';
import { TopResultsSection } from './TopResultsSection';

interface AudioResultsGridProps {
    audioItems?: SearchResult[];
}

// The tray shows at most this many cited results in a 2-column grid (three rows max).
const MAX_TOP_RESULTS = 6;

const GRID_CLASS_NAME = 'grid grid-cols-1 lg:grid-cols-2 gap-3';

export function AudioResultsGrid({ audioItems }: AudioResultsGridProps): JSX.Element {
    const items = audioItems ?? [];
    const { showTopResults } = useChatSessionContext();
    const topResults = useTopResults()
        .filter((result) => result.contentClass === 'audio')
        .slice(0, MAX_TOP_RESULTS);
    const hasTopResults = topResults.length > 0;

    return (
        <ActiveAudioItemProvider>
            {showTopResults ? (
                <div className="flex flex-col gap-2">
                    {hasTopResults && (
                        <TopResultsSection count={topResults.length}>
                            <div className={GRID_CLASS_NAME}>
                                {/* The tray is additive: cited items also remain in the full grid below, so
                                    the key is namespaced to stay unique across both renderings. */}
                                {topResults.map((result) => (
                                    <AudioResultCard key={`top-${result.contentId}`} result={result} isTopResult />
                                ))}
                            </div>
                        </TopResultsSection>
                    )}
                    <AllResultsSection count={items.length}>
                        <div className={GRID_CLASS_NAME}>
                            {items.map((result) => (
                                <AudioResultCard key={result.contentId} result={result} />
                            ))}
                        </div>
                    </AllResultsSection>
                </div>
            ) : (
                <div className={`${GRID_CLASS_NAME} p-4`}>
                    {items.map((result) => (
                        <AudioResultCard key={result.contentId} result={result} />
                    ))}
                </div>
            )}
            <AudioPlayer />
        </ActiveAudioItemProvider>
    );
}
